import * as React from 'react';
import { createPortal } from 'react-dom';
import Button, { type ButtonProps } from '@mui/material/Button';
import MuiMenu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

type Props = React.PropsWithChildren<{
  disabled?: boolean
  options: { label: string, onClick: () => void, disabled?: boolean }[]
}> & Omit<ButtonProps, 'children' | 'onClick' | 'disabled'>

export const Menu = React.forwardRef<HTMLButtonElement, Props>(function Menu({
  children,
  options,
  disabled,
  ...buttonProps
}, ref) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const menu = (
    <MuiMenu
      id="menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
    >
      {options.map((option, index) =>
        <MenuItem
          key={index.toString()}
          disabled={option.disabled}
          onClick={() => {
            option.onClick()
            handleClose()
          }}
        >
          {option.label}
        </MenuItem>
      )}
    </MuiMenu>
  );

  // Single Button root so MUI ButtonGroup can apply grouped styles via cloneElement.
  return (
    <Button
      ref={ref}
      id="menu-button"
      aria-controls={open ? 'menu-button' : undefined}
      aria-haspopup="true"
      aria-expanded={open ? 'true' : undefined}
      onClick={handleClick}
      disabled={!!disabled}
      size="small"
      variant="outlined"
      {...buttonProps}
    >
      {children}
      {typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
    </Button>
  );
})
